import React, { useCallback, useEffect, useState } from 'react';
import { cn } from '~/common/utils/styleUtil';
import { sanitizeDigits } from '~/common/utils/stringUtil';
import { LucideMinus, LucidePlus } from 'lucide-react';
import { LoginButton } from '~/shell/components/LoginButton';
import { AddArticleToTemplateButton } from '~/features/templates/routes/resources/AddArticleToTemplateRoute';
import { getArticleQuantityCache, setArticleQuantityCache } from '~/common/articleQuantityStorageClient.client.ts';
import { Media } from '~/media.ts';

const checkQuantity = (value: string, allowZero?: boolean) => {
  let number = Number(value);

  if (value === '' || (number === 0 && !allowZero)) {
    number = allowZero ? 0 : 1;
  }
  const isValid = !Number.isNaN(number) && (allowZero ? number >= 0 : number > 0) && number <= 1000;
  return { isValid, number };
};

export interface AddToTemplateProps {
  className?: string;
  mainArticleId: number;
  articleNumber?: string;
  articleDescriptionText?: string;
  isAuthenticated: boolean;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  calculatedTotalPriceText: string;
  disabled?: boolean;
  allowZero?: boolean;
  variant?: 'ghost' | 'default' | 'secondary' | 'primaryOutline' | 'link';
  buttonClassName?: string;
  cacheQuantity: boolean;
  showLargeOnDesktop?: boolean;
  forceSmallAddButton?: boolean;
}

export function AddToTemplate({
  mainArticleId,
  articleNumber,
  articleDescriptionText,
  className,
  isAuthenticated,
  quantity,
  onQuantityChange,
  disabled,
  allowZero,
  variant = 'link',
  buttonClassName,
  cacheQuantity,
  showLargeOnDesktop = false,
  forceSmallAddButton = false
}: Readonly<AddToTemplateProps>) {
  const [internalQuantityState, setInternalQuantityState] = useState<string>(quantity.toString());

  const setInternalQuantity = useCallback(
    (value: string) => {
      setInternalQuantityState(value);

      if (cacheQuantity) {
        setArticleQuantityCache(mainArticleId.toString(), +value);
      }
    },
    [mainArticleId, cacheQuantity]
  );

  const increment = e => {
    e.preventDefault();
    e.stopPropagation();
    setInternalQuantity((quantity + 1).toString());
    onQuantityChange(quantity + 1);
  };

  const decrement = e => {
    e.preventDefault();
    e.stopPropagation();
    setInternalQuantity((quantity - 1).toString());
    onQuantityChange(quantity - 1);
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();

    const value = sanitizeDigits(event.target.value);
    if (value === '') {
      setInternalQuantityState('');
      onQuantityChange(allowZero ? 0 : 1);
      return;
    }

    const { number, isValid } = checkQuantity(value, allowZero);

    if (isValid) {
      setInternalQuantity(number.toString());
      onQuantityChange(number);
    }
  };

  useEffect(() => {
    const loadQuantity = async () => {
      const cachedQuantity = await getArticleQuantityCache(mainArticleId.toString());
      const { number, isValid } = checkQuantity(cachedQuantity.toString(), allowZero);

      if (isValid && number?.toString() !== internalQuantityState) {
        setInternalQuantity(number.toString());
        onQuantityChange(number);
      }
    };

    if (cacheQuantity) {
      loadQuantity();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (quantity.toString() !== internalQuantityState && internalQuantityState !== '') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInternalQuantity(quantity.toString());
    }
  }, [quantity, internalQuantityState, setInternalQuantity]);

  const canDecrement = allowZero ? quantity >= 1 : quantity > 1;
  const canIncrement = quantity < 1000;
  const { isValid: isValidNumber } = checkQuantity(internalQuantityState, allowZero);
  const disabledStyles = 'text-neutral-400 border-neutral-200 hover:bg-white';

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div className={cn('flex justify-between', className)} onClick={e => e.stopPropagation()}>
      {!isAuthenticated && !disabled && (
        <>
          <Media lessThan={'desktop'} className={'flex-1'}>
            <LoginButton variant={'default'} size={'sm'} className={cn('w-full')} />
          </Media>
          <Media greaterThanOrEqual={'desktop'} className={'flex-1'}>
            <LoginButton variant={'default'} size={showLargeOnDesktop ? 'lg' : 'sm'} className={cn('w-full')} />
          </Media>
        </>
      )}
      {isAuthenticated && (
        <div className={cn('flex gap-2')}>
          <div
            className={cn(
              'flex items-center border rounded max-w-[7rem] h-9',
              showLargeOnDesktop && 'desktop:max-w-[9rem] desktop:h-11'
            )}
          >
            <button
              type="button"
              id="decrement-button"
              onClick={decrement}
              disabled={!canDecrement || disabled}
              className={cn(
                'bg-white text-primary hover:bg-primary-light h-full border-r rounded-l p-2',
                showLargeOnDesktop && 'desktop:p-4 desktop:aspect-square',
                !canDecrement && disabledStyles,
                !isValidNumber && disabledStyles
              )}
              data-testid={`addtotemplate-decrement-${mainArticleId}`}
            >
              <LucideMinus size={14} />
            </button>
            <input
              type="text"
              id={`template-quantity-input-${mainArticleId}`}
              value={internalQuantityState}
              name={'quantity'}
              onChange={handleAmountChange}
              className="font-bold text text-center block w-full outline-hidden"
              data-testid={`addtotemplate-amount-${mainArticleId}`}
              required
              disabled={disabled}
              onBlur={() => {
                if (internalQuantityState === '') {
                  setInternalQuantity(quantity.toString());
                }
              }}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onFocus={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseDown={e => {
                e.stopPropagation();
              }}
              onDoubleClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
            <button
              type="button"
              id="increment-button"
              disabled={!canIncrement || disabled}
              onClick={increment}
              className={cn(
                'bg-white text-primary hover:bg-primary-light h-full border-l rounded-r p-2',
                showLargeOnDesktop && 'desktop:p-4 desktop:aspect-square',
                !canIncrement && disabledStyles,
                !isValidNumber && disabledStyles
              )}
              data-testid={`addtotemplate-increment-${mainArticleId}`}
            >
              <LucidePlus size={14} />
            </button>
          </div>
          <AddArticleToTemplateButton
            articles={[{ mainArticleId, isExternalArticle: false, quantity, articleNumber, articleDescriptionText }]}
            isAgh={true}
            disabled={disabled}
            variant={variant}
            className={buttonClassName}
            forceSize={forceSmallAddButton ? 'small' : undefined}
          />
        </div>
      )}
    </div>
  );
}
